import { invalidate } from '@react-three/fiber';
import { useEffect } from 'react';
import { CameraHelper, OrthographicCamera, PerspectiveCamera } from 'three';
import { useShallow } from 'zustand/shallow';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const orthographicCamera = new OrthographicCamera(-10, 10, 10, -10, 0.01, 1000);
const perspectiveCamera = new PerspectiveCamera(50, 1, 0.01, 1000);
const orthographicCameraHelper = new CameraHelper(orthographicCamera);
const perspectiveCameraHelper = new CameraHelper(perspectiveCamera);

/**
 * FrustumCameraHelper — Visualizes the main scene camera's frustum in the viewer.
 *
 * Reads cameraHelperData from the frustum viewer store (kept in sync via postMessage)
 * and updates a Three.js CameraHelper accordingly.
 */
export const FrustumCameraHelper = () => {
    const cameraHelperData = usePlacementStore(useShallow((state) => state.cameraHelperData));

    useEffect(() => {
        if (!cameraHelperData) {
            return;
        }

        if (cameraHelperData.projectionType === CameraProjectionType.ORTHOGRAPHIC) {
            orthographicCamera.left = cameraHelperData.left;
            orthographicCamera.right = cameraHelperData.right;
            orthographicCamera.top = cameraHelperData.top;
            orthographicCamera.bottom = cameraHelperData.bottom;
            orthographicCamera.near = cameraHelperData.near;
            orthographicCamera.far = cameraHelperData.far;
            orthographicCamera.zoom = cameraHelperData.zoom ?? 1;
            orthographicCamera.matrixWorld.fromArray(cameraHelperData.matrixWorld);
            orthographicCamera.matrixWorldAutoUpdate = false;
            orthographicCamera.matrixWorldInverse.copy(orthographicCamera.matrixWorld).invert();
            orthographicCamera.updateProjectionMatrix();
            orthographicCameraHelper.update();
        } else {
            perspectiveCamera.fov = cameraHelperData.fov ?? perspectiveCamera.fov;
            perspectiveCamera.aspect = cameraHelperData.aspect ?? perspectiveCamera.aspect;
            perspectiveCamera.near = cameraHelperData.near;
            perspectiveCamera.far = cameraHelperData.far;
            perspectiveCamera.zoom = cameraHelperData.zoom ?? 1;
            perspectiveCamera.matrixWorld.fromArray(cameraHelperData.matrixWorld);
            perspectiveCamera.matrixWorldAutoUpdate = false;
            perspectiveCamera.matrixWorldInverse.copy(perspectiveCamera.matrixWorld).invert();
            perspectiveCamera.updateProjectionMatrix();
            perspectiveCameraHelper.update();
        }

        invalidate();
    }, [cameraHelperData]);

    return (
        <group>
            <primitive
                object={orthographicCameraHelper}
                visible={cameraHelperData?.projectionType === CameraProjectionType.ORTHOGRAPHIC}
            />
            <primitive
                object={perspectiveCameraHelper}
                visible={cameraHelperData?.projectionType === CameraProjectionType.PERSPECTIVE}
            />
        </group>
    );
};
